import { baseApi } from '@klp/core/api/services/rtkApi';
import { fromUTC } from '@klp/helpers';
import { User } from '@klp/user/classes/User';

import { userApiRoutes } from './api';
import type { IRegisterUserRequest, IUser, UserResponse } from './interfaces';

const toUser = (user: UserResponse): User =>
    new User({
        ...user,
        id: user.brukerId,
        email: user.epost,
        firstName: user.fornavn,
        lastName: user.etternavn,
        isAdmin: user.internalUser,
        customers: user.kundenummerList,
        welcomeMessageSent: user.velkomstMeldingSendt,
        isAdminUserAllowed: user.isAdminUserAllowed,
    });

const convertUserResponse = (userResponse: UserResponse): UserResponse => ({
    ...userResponse,
    velkomstMeldingSendt: userResponse.velkomstMeldingSendt
        ? new Date(fromUTC(userResponse.velkomstMeldingSendt))
        : undefined,
});

export const userApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        // Get current user
        getUser: builder.query<User, void>({
            query: () => ({
                url: userApiRoutes.userByToken(),
            }),
            transformResponse: (response: UserResponse) => toUser(response),
            providesTags: ['User'],
        }),

        // Search users
        searchUsers: builder.query<Array<IUser>, string>({
            query: (query) => ({
                url: userApiRoutes.userSearch(),
                params: { query },
            }),
            transformResponse: (response: Array<UserResponse>) =>
                response.map((userResponse) =>
                    toUser(convertUserResponse(userResponse)),
                ),
            providesTags: ['UserList'],
        }),

        // Get users for customer
        getUsersForCustomer: builder.query<
            Array<User>,
            { customerNumber: string; filterKundeportalInteresse?: boolean }
        >({
            query: ({ customerNumber, filterKundeportalInteresse }) => ({
                url: userApiRoutes.usersForCustomer(customerNumber),
                params: { filterKundeportalInteresse },
            }),
            transformResponse: (response: Array<UserResponse>) =>
                response.map((userResponse) =>
                    toUser(convertUserResponse(userResponse)),
                ),
            providesTags: (_result, _error, { customerNumber }) => [
                { type: 'CustomerUsers', id: customerNumber },
            ],
        }),

        // Register new user
        registerUser: builder.mutation<void, IRegisterUserRequest>({
            query: (user) => ({
                url: userApiRoutes.user(),
                method: 'POST',
                body: user,
            }),
            invalidatesTags: (_result, _error, arg) => [
                'UserList',
                { type: 'CustomerUsers', id: arg.customerNumber },
            ],
        }),

        // Send welcome message
        sendWelcomeMessage: builder.mutation<
            void,
            { email: string; culture: string }
        >({
            query: ({ email, culture }) => ({
                url: userApiRoutes.userNotifyWelcome(),
                method: 'POST',
                body: { userName: email, culture },
            }),
            invalidatesTags: ['User', 'UserList', 'CustomerUsers'],
        }),

        // Grant admin
        grantAdmin: builder.mutation<void, string>({
            query: (email) => ({
                url: userApiRoutes.adminGrant(email),
                method: 'PUT',
            }),
            invalidatesTags: ['User', 'UserList', 'CustomerUsers'],
        }),

        // Revoke admin
        revokeAdmin: builder.mutation<void, string>({
            query: (email) => ({
                url: userApiRoutes.adminRevoke(email),
                method: 'PUT',
            }),
            invalidatesTags: ['User', 'UserList', 'CustomerUsers'],
        }),

        // Grant super user
        grantSuperUser: builder.mutation<void, string>({
            query: (email) => ({
                url: userApiRoutes.superuserGrant(email),
                method: 'PUT',
            }),
            invalidatesTags: ['User', 'UserList', 'CustomerUsers'],
        }),

        // Revoke super user
        revokeSuperUser: builder.mutation<void, string>({
            query: (email) => ({
                url: userApiRoutes.superuserRevoke(email),
                method: 'PUT',
            }),
            invalidatesTags: ['User', 'UserList', 'CustomerUsers'],
        }),

        // Add customer to user
        addCustomer: builder.mutation<
            void,
            { customerNumber: string; email: string }
        >({
            query: ({ customerNumber, email }) => ({
                url: userApiRoutes.userCustomer(),
                method: 'PUT',
                body: { customerNumber, email },
            }),
            invalidatesTags: (_result, _error, { customerNumber }) => [
                'User',
                'UserList',
                { type: 'CustomerUsers', id: customerNumber },
            ],
        }),

        // Remove customer from user
        removeCustomer: builder.mutation<
            void,
            { customerNumber: string; email: string }
        >({
            query: ({ customerNumber, email }) => ({
                url: userApiRoutes.userCustomer(),
                method: 'DELETE',
                body: { customerNumber, email },
            }),
            invalidatesTags: (_result, _error, { customerNumber }) => [
                'User',
                'UserList',
                { type: 'CustomerUsers', id: customerNumber },
            ],
        }),

        // Remove user
        removeUser: builder.mutation<void, string>({
            query: (email) => ({
                url: userApiRoutes.userDelete(email),
                method: 'DELETE',
            }),
            invalidatesTags: ['User', 'UserList', 'CustomerUsers'],
        }),
    }),
});

export const {
    useGetUserQuery,
    useSearchUsersQuery,
    useLazySearchUsersQuery,
    useGetUsersForCustomerQuery,
    useRegisterUserMutation,
    useSendWelcomeMessageMutation,
    useGrantAdminMutation,
    useRevokeAdminMutation,
    useGrantSuperUserMutation,
    useRevokeSuperUserMutation,
    useAddCustomerMutation,
    useRemoveCustomerMutation,
    useRemoveUserMutation,
} = userApi;
